import { useEffect, useRef, useState } from 'react'

type HookProps = {
  progress: number
  isError: boolean
  isProcessing: boolean
  onProgressShown?: () => void
}

const useUploadProgress = ({
  progress,
  isError,
  onProgressShown,
  isProcessing,
}: HookProps) => {
  const initialProgressRef = useRef(progress)
  const alreadyDone = initialProgressRef.current >= 100

  const [isSuccessVisible, setIsSuccessVisible] = useState(false)
  const [isHidden, setIsHidden] = useState(alreadyDone)
  const hideTimerRef = useRef<number | null>(null)
  const successTimerRef = useRef<number | null>(null)

  useEffect(() => {
    if (isProcessing) {
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current)
        hideTimerRef.current = null
      }
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current)
        successTimerRef.current = null
      }
      setIsSuccessVisible(false)
      setIsHidden(false)
      return
    }
    if (isError) {
      setIsSuccessVisible(false)
      setIsHidden(false)
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current)
        hideTimerRef.current = null
      }
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current)
        successTimerRef.current = null
      }
      return
    }

    if (progress >= 100 && isHidden) {
      onProgressShown?.()
      return
    }

    if (progress >= 100 && !isHidden) {
      if (!successTimerRef.current) {
        successTimerRef.current = window.setTimeout(() => {
          successTimerRef.current = null
          if (!isError && progress >= 100 && !isHidden) {
            setIsSuccessVisible(true)
            hideTimerRef.current = window.setTimeout(() => {
              onProgressShown?.()

              setIsHidden(true)
            }, 2000)
          }
        }, 3000)
      }
    } else {
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current)
        successTimerRef.current = null
      }
    }

    return () => {
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current)
        hideTimerRef.current = null
      }
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current)
        successTimerRef.current = null
      }
    }
  }, [progress, isHidden, isError, isProcessing])

  return {
    isHidden,
    isSuccessVisible,
  }
}

export default useUploadProgress
